// components/customComponents/Modals/ModalProtocolos.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Col, Row } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-quill/dist/quill.snow.css';

import { TablaEscalamientos } from '../../customFunctionalComponents/components/TablaEscalamientos';
import { ChatGroupsManager } from '../../customFunctionalComponents/components/ChatGroupsManager';

export const ModalProtocolos = ({
    show,
    onHide,
    onSave,
    protocoloToEdit,
    hasProtocols,
    onPostEscalamientoFromParent,
    onDeleteEscalamientoFromParent,
    onDeleteGruposChatFromParent,
    onPutGruposChatFromParent,
    onPostGruposChatFromParent,
    onGetGruposChatFromParent
}) => {
    const [protocoloTitle, setProtocoloTitle] = useState('');
    const [protocoloContent, setProtocoloContent] = useState('');
    const [localEscalamientos, setLocalEscalamientos] = useState([]);
    const [localGruposChat, setLocalGruposChat] = useState([]);

    const escalamientosTableRef = useRef(null);
    const chatGroupsManagerRef = useRef(null);

    useEffect(() => {
        if (show) {
            // console.log("[ModalProtocolos] Modal is showing. Checking protocoloToEdit...");
            // console.log("[ModalProtocolos] protocoloToEdit recibido en useEffect:", protocoloToEdit);

            if (protocoloToEdit) {
                // console.log("[ModalProtocolos] Rellenando campos con datos existentes.");
                setProtocoloTitle(protocoloToEdit.titulo_protocolo || '');
                setProtocoloContent(protocoloToEdit.evento_riesgo || '');
                setLocalEscalamientos(protocoloToEdit.escalamientos || []);
                setLocalGruposChat(protocoloToEdit.grupos_mensajeria || []);
            } else {
                // console.log("[ModalProtocolos] No hay protocoloToEdit. Inicializando para nuevo protocolo.");
                setProtocoloTitle('');
                setProtocoloContent('');
                setLocalEscalamientos([]);
                setLocalGruposChat([]);
            }
        }
    }, [show, protocoloToEdit]);

    // Log para ver los valores actuales de los estados justo antes de renderizar
    // console.log("[ModalProtocolos - Render] protocoloTitle:", protocoloTitle);
    // console.log("[ModalProtocolos - Render] protocoloContent:", protocoloContent);
    // console.log("[ModalProtocolos - Render] localEscalamientos:", localEscalamientos);
    // console.log("[ModalProtocolos - Render] localGruposChat:", localGruposChat);


    const handleGuardarCambios = async () => {
        if (!protocoloTitle.trim()) {
            toast.error("El título del protocolo no puede estar vacío.");
            return;
        }

        const plainTextContent = protocoloContent.replace(/<[^>]+>/g, '');
        if (!plainTextContent.trim()) {
            toast.error("El contenido del protocolo no puede estar vacío.");
            return;
        }

        let currentProtocolId = protocoloToEdit ? protocoloToEdit.id : null;
        let currentCedisId = protocoloToEdit ? protocoloToEdit.cedis_id : null;
        const isNewProtocol = !currentProtocolId || String(currentProtocolId).startsWith('TEMP_');

        // Paso 1: Guardar el Protocolo Principal (si es nuevo o si se modificó)
        // console.log("[ModalProtocolos - Guardar Cambios] Iniciando guardado de protocolo principal.");
        if (onSave) {
            const protocolSaveResult = await onSave({
                ...protocoloToEdit, // Pasa los datos originales para mantener cedis_id si es nuevo
                titulo_protocolo: protocoloTitle,
                evento_riesgo: protocoloContent,
            });

            if (!protocolSaveResult.ok) {
                toast.error(protocolSaveResult.msg || "Error al guardar el protocolo principal.");
                return; // Detener si el protocolo principal no se guardó
            }

            // Si es un protocolo nuevo, actualiza los IDs con los generados por el backend
            if (isNewProtocol) {
                currentProtocolId = protocolSaveResult.protocolId;
                currentCedisId = protocolSaveResult.cedisId; // Asegurarse de tener el cedisId real
                // console.log("[ModalProtocolos - Guardar Cambios] Protocolo principal nuevo guardado. ID:", currentProtocolId, "CEDIS ID:", currentCedisId);
                // Actualizar protocoloToEdit para las siguientes operaciones
                protocoloToEdit = {
                    ...protocoloToEdit,
                    id: currentProtocolId,
                    cedis_id: currentCedisId,
                };
            }
            toast.success("Protocolo guardado con éxito!");
        } else {
            console.error("[ModalProtocolos - Guardar Cambios] La función onSave no está disponible.");
            toast.error("Error interno: No se pudo guardar el protocolo principal.");
            return;
        }

        // Paso 2: Guardar Escalamientos (solo los nuevos)
        // console.log("[ModalProtocolos - Guardar Cambios] Iniciando guardado de escalamientos.");
        if (escalamientosTableRef.current && onPostEscalamientoFromParent && currentProtocolId) {
            const currentEscalamientos = escalamientosTableRef.current.getEscalamientosData();
            const newEscalamientosToSave = currentEscalamientos.filter(esc => String(esc.id).startsWith('TEMP_'));
            // console.log("[ModalProtocolos - Guardar Cambios] Nuevos escalamientos a guardar:", newEscalamientosToSave);

            for (const escalamiento of newEscalamientosToSave) {
                const payload = {
                    ...escalamiento,
                    protocolo_id: currentProtocolId // Usar el ID real del protocolo
                };
                delete payload.id; // Eliminar el ID temporal interno del frontend
                delete payload.escalamiento_id; // Eliminar escalamiento_id (ya es null para nuevos)

                // console.log("[ModalProtocolos] Guardando nuevo escalamiento:", payload);
                const response = await onPostEscalamientoFromParent(payload);
                if (!response.ok) {
                    toast.error(`Error al guardar escalamiento ${escalamiento.nombre_completo}: ${response.msg || "Error desconocido"}`);
                } else {
                    // toast.success(`Escalamiento ${escalamiento.nombre_completo} guardado con éxito!`);
                }
            }
        } else {
            // console.log("[ModalProtocolos - Guardar Cambios] No hay escalamientos nuevos o dependencias no disponibles.");
        }

        // Paso 3: Guardar Grupos de Chat (solo los nuevos)
        // console.log("[ModalProtocolos - Guardar Cambios] Iniciando guardado de grupos de chat.");
        if (chatGroupsManagerRef.current && onPostGruposChatFromParent && currentCedisId) {
            const currentGroups = chatGroupsManagerRef.current.getChatGroupsData();
            const newGroupsToSave = currentGroups.filter(group => String(group.id).startsWith('TEMP_'));
            // console.log("[ModalProtocolos - Guardar Cambios] Nuevos grupos de chat a guardar:", newGroupsToSave);

            for (const group of newGroupsToSave) {
                const payload = {
                    ...group,
                    cedis_id: currentCedisId // Usar el CEDIS ID real
                };
                delete payload.id; // Eliminar el ID temporal interno del frontend
                delete payload.grupo_mensajeria_id; // Eliminar grupo_mensajeria_id (ya es null para nuevos)

                // console.log("[ModalProtocolos] Guardando nuevo grupo de chat:", payload);
                const response = await onPostGruposChatFromParent(payload);
                if (!response.ok) {
                    toast.error(`Error al guardar grupo de chat ${group.nombre_grupo}: ${response.msg || "Error desconocido"}`);
                } else {
                    // toast.success(`Grupo de chat ${group.nombre_grupo} guardado con éxito!`);
                }
            }
        } else {
            // console.log("[ModalProtocolos - Guardar Cambios] No hay grupos de chat nuevos o dependencias no disponibles.");
        }

        // Finalmente, cerrar el modal y recargar los datos en el padre
        handleCerrarModal();
    };

    const handlePostEscalamiento = async (escalamientoData) => {
        console.warn("[ModalProtocolos] handlePostEscalamiento llamado, pero el guardado de POST escalamientos ahora se maneja en handleGuardarCambios.");
        return { ok: false, msg: "Función no utilizada para POST directo de escalamientos." };
    };

    const handleDeleteEscalamiento = async (escalamientoId) => {
        const response = await onDeleteEscalamientoFromParent(escalamientoId);
        if (response.ok) {
            toast.success("Escalamiento eliminado con éxito!");
        } else {
            toast.error(response.msg || "Error al eliminar escalamiento.");
        }
        return response;
    };

    // Estas funciones ahora se llaman desde handleGuardarCambios para nuevos grupos
    // y directamente desde ChatGroupsManager para PUT/DELETE
    const handlePostChatGroup = async (groupData) => {
        console.warn("[ModalProtocolos] handlePostChatGroup llamado, pero el guardado de POST grupos de chat ahora se maneja en handleGuardarCambios.");
        return { ok: false, msg: "Función no utilizada para POST directo de grupos de chat." };
    };

    const handlePutChatGroup = async (groupId, groupData) => {
        const response = await onPutGruposChatFromParent(groupId, groupData);
        if (response.ok) {
            toast.success("Grupo de chat actualizado con éxito!");
            await reloadGruposChat();
        } else {
            toast.error(response.msg || "Error al actualizar grupo de chat.");
        }
        return response;
    };

    const handleDeleteChatGroup = async (groupId) => {
        const response = await onDeleteGruposChatFromParent(groupId);
        if (response.ok) {
            toast.success("Grupo de chat eliminado con éxito!");
            await reloadGruposChat();
        } else {
            toast.error(response.msg || "Error al eliminar grupo de chat.");
        }
        return response;
    };

    const reloadGruposChat = async () => {
        if (protocoloToEdit && protocoloToEdit.cedis_id && onGetGruposChatFromParent) {
            const response = await onGetGruposChatFromParent(protocoloToEdit.cedis_id);
            if (response.ok && response.grupos) {
                setLocalGruposChat(response.grupos);
                // toast.info("Grupos de chat actualizados.");
            } else {
                toast.error(response.msg || "No se pudieron recargar los grupos de chat.");
            }
        }
    };


    const handleCerrarModal = () => {
        onHide();
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'color': [] }, {'background': []}],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            ['clean']
        ],
    };

    const formats = [
        'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
        'color' ,'background','list', 'bullet', 'indent'
    ];

    const title = hasProtocols ? "EDITAR PROTOCOLO" : "AGREGAR PROTOCOLO";

    return (
        <Modal show={show} onHide={handleCerrarModal} centered size="xl">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3" controlId="protocoloTitle">
                        <Form.Label>Título del Protocolo</Form.Label>
                        <Form.Control
                            key={protocoloToEdit ? `${protocoloToEdit.id}-title` : 'new-protocol-title'}
                            type="text"
                            placeholder="Ingrese el título del protocolo"
                            value={protocoloTitle}
                            onChange={(e) => setProtocoloTitle(e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="protocoloContent">
                        <Form.Label>Contenido del Protocolo</Form.Label>
                        <ReactQuill
                            key={protocoloToEdit ? `${protocoloToEdit.id}-content` : 'new-protocol-content'}
                            theme="snow"
                            value={protocoloContent}
                            onChange={setProtocoloContent}
                            modules={modules}
                            formats={formats}
                            className='custom-quill-editor'
                            placeholder="Ingrese el contenido del protocolo aquí..."
                            style={{
                                minHeight: '200px', // Altura mínima
                                maxHeight: '500px', // Altura
                                resize: 'vertical', // Permite redimensionar verticalmente
                                overflow: 'auto' // Muestra barras de desplazamiento si el contenido excede el tama
                            }}
                        />
                    </Form.Group>
                    <div style={{ height: '50px' }}></div>

                    <TablaEscalamientos
                        ref={escalamientosTableRef}
                        key={protocoloToEdit ? `${protocoloToEdit.id}-escalamientos` : 'new-escalamientos-table'}
                        initialEscalamientos={localEscalamientos}
                        protocoloId={protocoloToEdit ? protocoloToEdit.id : null}
                        onPostEscalamiento={handlePostEscalamiento}
                        onDeleteEscalamiento={handleDeleteEscalamiento}
                    />
                    <div style={{ height: '50px' }}></div>

                    <ChatGroupsManager
                        ref={chatGroupsManagerRef}
                        key={protocoloToEdit ? `${protocoloToEdit.id}-chat-manager` : 'new-chat-manager'}
                        initialGroups={localGruposChat}
                        cedisId={protocoloToEdit ? protocoloToEdit.cedis_id : null}
                        onPostChatGroup={handlePostChatGroup} // Ahora es un placeholder
                        onPutChatGroup={handlePutChatGroup}
                        onDeleteChatGroup={handleDeleteChatGroup}
                    />

                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCerrarModal}>
                    Cerrar
                </Button>
                <Button variant="primary" onClick={handleGuardarCambios}>
                    Guardar cambios
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
